import { X } from "lucide-react";
import { Button, EmptyState, Tags } from "../ui";
import { categoryLabel } from "../copilot/copilotText";
import { CopyButton, StatusTag, formatDateTime } from "./parts";

function Row({ label, children, copy, t, dir }) {
  return (
    <div className="ca-row">
      <dt>{label}</dt>
      <dd>
        <span className="ca-row-value" dir={dir}>{children}</span>
        {copy ? <CopyButton value={copy} label={label} t={t} /> : null}
      </dd>
    </div>
  );
}

// Customer + conversation details. Only what support needs: contact data given
// in the conversation, the related restaurant/branch (names, no account data).
export default function DetailsPane({ inbox, lang, access, onClose, onAskResolve, onAskAssign }) {
  const { t, detail, categories } = inbox;
  const c = detail.conversation;
  if (!c) return <EmptyState>{t.selectOneBody}</EmptyState>;

  const restaurantName = typeof c.restaurant === "object" && c.restaurant ? c.restaurant.name || c.restaurant.code : null;
  const branchName = typeof c.branch === "object" && c.branch ? c.branch.name : null;
  const dash = t.none;

  return (
    <div className="ca-details">
      <header className="ca-details-head">
        <h3>{t.details}</h3>
        <Button variant="neutral" appearance="ghost" size="sm" className="ca-details-close" onClick={onClose} aria-label={t.closeDetails} title={t.closeDetails} icon={<X size={18} aria-hidden="true" />} />
      </header>
      <div className="ca-details-scroll">
        <section aria-labelledby="ca-d-customer">
          <h4 id="ca-d-customer">{t.customer} <Tags size="sm" variant={c.customer.kind === "guest" ? "warning" : "primary"}>{c.customer.kind === "guest" ? t.guest : t.registeredUser}</Tags></h4>
          <dl>
            <Row label={t.name} t={t} copy={c.customer.name}>{c.customer.name}</Row>
            <Row label={t.email} t={t} copy={c.customer.email} dir="ltr">{c.customer.email}</Row>
            <Row label={t.phone} t={t} copy={c.customer.phone} dir="ltr">{c.customer.phone || t.notProvided}</Row>
          </dl>
        </section>

        <section aria-labelledby="ca-d-conv">
          <h4 id="ca-d-conv">{t.conversation}</h4>
          <dl>
            <Row label={t.reference} t={t} copy={c.reference} dir="ltr">{c.reference}</Row>
            <Row label={t.status} t={t}><StatusTag status={c.status} t={t} /></Row>
            <Row label={t.category} t={t}>{categoryLabel(categories, c.category, lang)}</Row>
            {c.faq?.question && <Row label={t.relatedFaq} t={t}>{c.faq.question}</Row>}
            <Row label={t.created} t={t}>{formatDateTime(c.createdAt, lang)}</Row>
            <Row label={t.lastActivity} t={t}>{formatDateTime(c.lastActivityAt, lang)}</Row>
            <Row label={t.firstResponse} t={t}>{c.firstResponseAt ? formatDateTime(c.firstResponseAt, lang) : dash}</Row>
            {c.reopenCount > 0 && <Row label={t.status} t={t}>{t.reopened(c.reopenCount)}</Row>}
          </dl>
        </section>

        {(restaurantName || branchName) && (
          <section aria-labelledby="ca-d-where">
            <h4 id="ca-d-where">{t.restaurant}</h4>
            <dl>
              <Row label={t.restaurant} t={t} copy={restaurantName}>{restaurantName || dash}</Row>
              {branchName && <Row label={t.branch} t={t} copy={branchName}>{branchName}</Row>}
            </dl>
          </section>
        )}

        <section aria-labelledby="ca-d-assign">
          <h4 id="ca-d-assign">{t.assignedTo}</h4>
          <p className="ca-assigned">
            {c.assignedTo ? <strong>{c.assignedTo.name || t.assignedAgentBadge}</strong> : <span className="ca-muted">{t.unassignedLabel}</span>}
            {c.assignedAt && <span className="ca-muted"> · {formatDateTime(c.assignedAt, lang)}</span>}
          </p>
          {access.assign && <Button size="sm" variant="neutral" appearance="outline" disabled={Boolean(inbox.busy)} onClick={onAskAssign}>{c.assignedTo ? t.reassign : t.assign}</Button>}
        </section>

        {c.resolution && (
          <section aria-labelledby="ca-d-res">
            <h4 id="ca-d-res">{t.resolution}</h4>
            <dl>
              <Row label={t.resolvedAt} t={t}>{formatDateTime(c.resolution.resolvedAt, lang)}</Row>
              {c.resolution.resolvedBy?.name && <Row label={t.resolvedBy} t={t}>{c.resolution.resolvedBy.name}</Row>}
              {c.resolution.note && <Row label={t.note} t={t} copy={c.resolution.note}>{c.resolution.note}</Row>}
            </dl>
          </section>
        )}

        {access.changeStatus && (
          <section aria-labelledby="ca-d-status">
            <h4 id="ca-d-status">{t.changeStatus}</h4>
            <div className="ca-status-buttons">
              {c.status === "resolved" ? (
                <Button size="sm" variant="primary" appearance="filled" disabled={Boolean(inbox.busy)} onClick={() => inbox.changeStatus("open")}>{t.reopen}</Button>
              ) : (
                <>
                  {c.status !== "open" && <Button size="sm" variant="neutral" appearance="outline" disabled={Boolean(inbox.busy)} onClick={() => inbox.changeStatus("open")}>{t.markOpen}</Button>}
                  {c.status !== "pending" && <Button size="sm" variant="neutral" appearance="outline" disabled={Boolean(inbox.busy)} onClick={() => inbox.changeStatus("pending")}>{t.markPending}</Button>}
                  <Button size="sm" variant="success" appearance="filled" disabled={Boolean(inbox.busy)} onClick={onAskResolve}>{t.resolve}</Button>
                </>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
